import React, { useContext, useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { AnimatePresence, motion } from 'motion/react'
import { Mail, AlertTriangle, CheckCircle2 } from "lucide-react"
import heroArt from "../assets/images/honeyguide_hero.png"
import { postLogin, postLoginLinkCode, postRequestLoginLink } from "../api/client"
import { UserContext } from "../components/UserContext"
import RecaptchaNotice from "../components/RecaptchaNotice"
import { useNotify } from "../components/notice/useNotify"
import { isProduction } from "../utils/environment"

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY

type AuthMode = "password" | "link"

const SURFACE_SPRING = { type: 'spring' as const, stiffness: 500, damping: 30 }
const BUTTON_SPRING = { type: 'spring' as const, stiffness: 400, damping: 20 }

// One email form for everyone: the server sends a sign-in link to an address
// it knows and a sign-up link to one it doesn't, so the page never asks which.
// Password sign-in is a development convenience with no control on the page:
// on a dev or staging bundle `/login?password` opens its form (the e2e login
// helper and the dev-clone test login use it), and on a prod bundle nothing
// sets `authMode` to "password", so the form is unreachable rather than
// merely hidden.
//
// This is the *affordance*, not a lock: `POST /api/login` still accepts a
// password on every host. Someone who knows the API can still use one, which
// is what makes the dev accounts' passwords worth being real ones.

const NOISE_SVG =
    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'>" +
    "<filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter>" +
    "<rect width='100%' height='100%' filter='url(%23n)' opacity='0.7'/></svg>\")"

const LoginPage = () => {
    const { refreshUser } = useContext(UserContext)
    const notify = useNotify()
    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const authMode: AuthMode =
        !isProduction() && searchParams.has("password") ? "password" : "link"
    const [linkSent, setLinkSent] = useState(false)
    const [code, setCode] = useState("")
    const showValidationSuccess = searchParams.get("from_validation") === "true"
    const emailTaken = searchParams.get("login_link_email_taken") === "true"
    const accountDeleted = searchParams.get("account_deleted") === "true"

    const [error, setError] = useState("")
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        if (authMode === "link") {
            const existing = document.querySelector(`script[src*="recaptcha/api.js"]`)
            if (existing) return
            const script = document.createElement("script")
            script.src = "https://www.google.com/recaptcha/api.js?render=" + RECAPTCHA_SITE_KEY
            document.body.appendChild(script)
        }
    }, [authMode])

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsLoading(true)
        try {
            const response = await postLogin({ email, password })
            if (response.user) {
                await refreshUser()
            }
        } catch (error) {
            console.error("Login failed:", error)
            setError(error.response?.data?.error || "Login failed. Please check your credentials.")
        } finally {
            setIsLoading(false)
        }
    }

    const handleLinkSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!email || !email.includes("@")) {
            setError("Please enter a valid email address.")
            return
        }
        setError("")
        setIsLoading(true)
        try {
            const recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
                action: "login_link"
            })
            await postRequestLoginLink({ email, recaptcha_token: recaptchaToken })
            setLinkSent(true)
        } catch (error) {
            console.error("Login link request failed:", error)
            setError(error.response?.data?.error || "Could not send a code. Please try again.")
        } finally {
            setIsLoading(false)
        }
    }

    // The code from the same email as the link. It is the way in from the iOS
    // app, where a tapped link opens a browser instead of the app, and works
    // anywhere the email can be read.
    const handleCodeSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError("")
        setIsLoading(true)
        try {
            const { next } = await postLoginLinkCode({ email, code })
            if (next === "/") {
                await refreshUser()
            } else {
                navigate(next)
            }
        } catch (error) {
            console.error("Login code failed:", error)
            setError(error.response?.data?.error || "That code didn't work. Please try again.")
        } finally {
            setIsLoading(false)
        }
    }

    // A one-shot confirmation, so it goes on the app-wide notice. The two
    // banners below stay inline: they are instructions for this form.
    useEffect(() => {
        if (showValidationSuccess) {
            notify.success("Email address confirmed.")
        }
    }, [showValidationSuccess, notify])

    const isLink = authMode === "link"

    return (
        <div className="relative flex justify-center px-4 py-8 sm:py-14">
            {/* Tinted gradient stage */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 -z-10"
                style={{
                    background:
                        "radial-gradient(55% 45% at 50% 12%, rgba(252,211,77,0.28) 0%, rgba(252,211,77,0) 65%)," +
                        "radial-gradient(60% 50% at 100% 90%, rgba(244,114,182,0.18) 0%, rgba(244,114,182,0) 60%)," +
                        "radial-gradient(60% 50% at 0% 100%, rgba(167,139,250,0.12) 0%, rgba(167,139,250,0) 60%)," +
                        "linear-gradient(180deg, #fdfaf4 0%, #fbf3e8 100%)",
                }}
            />
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 -z-10 opacity-[0.04] mix-blend-multiply"
                style={{ backgroundImage: NOISE_SVG }}
            />

            <div className="w-full max-w-md">
                {/* Search-param banners */}


                {emailTaken && (
                    <div className="mb-3 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-amber-900 backdrop-blur">
                        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" strokeWidth={2} />
                        <span className="text-sm">An account with that email already exists. Sign in below.</span>
                    </div>
                )}

                {accountDeleted && (
                    <div className="mb-3 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/80 px-4 py-3 text-emerald-800 backdrop-blur">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" strokeWidth={2} />
                        <span className="text-sm">Your account has been deleted.</span>
                    </div>
                )}

                {/* Card */}
                <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={SURFACE_SPRING}
                    className="relative rounded-3xl border border-stone-200/70 bg-white/95 px-6 py-9 sm:px-9 sm:py-11 shadow-[0_30px_60px_-30px_rgba(120,53,15,0.18),0_8px_24px_-12px_rgba(120,53,15,0.10)] backdrop-blur"
                >
                    {/* Hairline gradient atop the card */}
                    <div
                        aria-hidden
                        className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-amber-300/70 to-transparent"
                    />

                    <div className="flex flex-col items-center text-center">
                        <motion.img
                            src={heroArt}
                            alt=""
                            width="150"
                            height="164"
                            className="mb-1"
                            initial={{ opacity: 0, scale: 0.6 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ ...SURFACE_SPRING, delay: 0.04 }}
                        />
                        <motion.h1
                            className="font-serif text-3xl tracking-tight text-stone-900"
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ ...SURFACE_SPRING, delay: 0.08 }}
                        >
                            Honeyguide
                        </motion.h1>
                    </div>


                    {/* Form area */}
                    <motion.div
                        className="mt-6"
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ ...SURFACE_SPRING, delay: 0.24 }}
                    >
                        <AnimatePresence mode="wait" initial={false}>
                            {authMode === "password" && (
                                <motion.form
                                    key="password"
                                    onSubmit={handlePasswordSubmit}
                                    initial={{ opacity: 0, y: -6, scale: 0.97 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -6, scale: 0.97 }}
                                    transition={SURFACE_SPRING}
                                >
                                    <FieldLabel htmlFor="email">Email</FieldLabel>
                                    <PrettyInput
                                        id="email"
                                        type="text"
                                        placeholder="you@somewhere.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                    <div className="mt-4">
                                        <FieldLabel htmlFor="password">Password</FieldLabel>
                                        <PrettyInput
                                            id="password"
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                        />
                                    </div>
                                    {error && <ErrorMessage>{error}</ErrorMessage>}
                                    <SubmitButton loading={isLoading} loadingLabel="Logging in…">Log in</SubmitButton>
                                </motion.form>
                            )}

                            {isLink && !linkSent && (
                                <motion.form
                                    key="link"
                                    onSubmit={handleLinkSubmit}
                                    initial={{ opacity: 0, y: -6, scale: 0.97 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -6, scale: 0.97 }}
                                    transition={SURFACE_SPRING}
                                >
                                    <p className="mb-3 text-sm text-stone-600">
                                        We'll email you a code to sign in or sign up.
                                    </p>
                                    <FieldLabel htmlFor="link-email">Email</FieldLabel>
                                    <PrettyInput
                                        id="link-email"
                                        type="email"
                                        placeholder="you@somewhere.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                    />
                                    {error && <ErrorMessage>{error}</ErrorMessage>}
                                    <SubmitButton loading={isLoading} loadingLabel="Sending…">Send</SubmitButton>
                                </motion.form>
                            )}

                            {isLink && linkSent && (
                                <motion.div
                                    key="link-sent"
                                    initial={{ opacity: 0, y: -6, scale: 0.97 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -6, scale: 0.97 }}
                                    transition={SURFACE_SPRING}
                                    className="rounded-2xl border border-emerald-200 bg-emerald-50/80 p-5"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                                            <Mail className="h-4 w-4" strokeWidth={2.25} />
                                        </div>
                                        <div className="text-left">
                                            <div className="font-medium text-emerald-900">Check your email.</div>
                                            <div className="mt-0.5 text-sm text-emerald-800">
                                                We sent a code to <span className="font-medium">{email}</span>. It expires in 15 minutes.
                                            </div>
                                            <motion.button
                                                type="button"
                                                onClick={() => { setLinkSent(false); setCode(""); setError("") }}
                                                whileHover={{ scale: 1.04 }}
                                                whileTap={{ scale: 0.96 }}
                                                transition={BUTTON_SPRING}
                                                className="mt-3 text-sm font-medium text-emerald-800 underline underline-offset-2 hover:text-emerald-900"
                                            >
                                                Send another
                                            </motion.button>
                                        </div>
                                    </div>
                                    <form onSubmit={handleCodeSubmit} className="mt-5 border-t border-emerald-200 pt-4">
                                        <FieldLabel htmlFor="login-code">Code</FieldLabel>
                                        <PrettyInput
                                            id="login-code"
                                            type="text"
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                            placeholder="123 456"
                                            value={code}
                                            onChange={(e) => setCode(e.target.value)}
                                            required
                                        />
                                        {error && <ErrorMessage>{error}</ErrorMessage>}
                                        <SubmitButton loading={isLoading} loadingLabel="Logging in…">Log in</SubmitButton>
                                    </form>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>

                    <RecaptchaNotice className="mt-6" />
                    <p className="mt-2 text-center text-[11px] text-stone-400">
                        Honeyguide is a product of Shaker Vibes LLC.{" "}
                        <a
                            href="/static/privacy_policy.txt"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline underline-offset-2 hover:text-stone-600"
                        >
                            Privacy policy
                        </a>
                    </p>
                </motion.div>
            </div>
        </div>
    )
}

const FieldLabel = ({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) => (
    <label htmlFor={htmlFor} className="mb-1.5 block text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500">
        {children}
    </label>
)

const PrettyInput = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
    <input
        {...props}
        className="w-full rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-stone-900 placeholder:text-stone-400 transition focus:border-amber-400 focus:outline-none focus:ring-4 focus:ring-amber-400/20"
    />
)

const ErrorMessage = ({ children }: { children: React.ReactNode }) => (
    <motion.div
        initial={{ opacity: 0, y: -4, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={SURFACE_SPRING}
        role="alert"
        className="mt-3 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
    >
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} />
        <span className="whitespace-pre-line text-left">{children}</span>
    </motion.div>
)

const SubmitButton = ({ loading, loadingLabel, children }: { loading: boolean; loadingLabel: string; children: React.ReactNode }) => (
    <motion.button
        type="submit"
        disabled={loading}
        whileHover={loading ? undefined : { scale: 1.015, y: -1 }}
        whileTap={loading ? undefined : { scale: 0.985 }}
        transition={BUTTON_SPRING}
        className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-action to-action-dark px-4 py-2.5 text-sm font-medium text-plum shadow-[0_8px_24px_-10px_rgba(180,83,9,0.55)] disabled:cursor-not-allowed disabled:opacity-60 hover:shadow-[0_10px_28px_-10px_rgba(180,83,9,0.65)]"
    >
        {loading ? (
            <>
                <SpinnerSvg />
                {loadingLabel}
            </>
        ) : children}
    </motion.button>
)

const SpinnerSvg = () => (
    <svg className="-ml-1 h-4 w-4 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
)

export default LoginPage
